import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Writes URL search params by MERGING into the live URL.
 *
 * Every data grid keeps its state in the URL (`q`, `page`, `pageSize`,
 * `sortBy`, `sortDir`, `density`, `hidden`, `f.*`) through nuqs - see
 * `useDataGridState`. nuqs updates the address bar with a raw
 * `history.replaceState` and never routes it through React Router, so React
 * Router's own `useSearchParams()` snapshot is FROZEN at the last real
 * navigation and knows nothing about those keys.
 *
 * That makes the obvious-looking writer wrong:
 *
 *     const [params, setParams] = useSearchParams();
 *     const next = new URLSearchParams(params);   // <- pre-grid snapshot
 *     next.set('tab', 'media');
 *     setParams(next, { replace: true });         // <- grid state gone
 *
 * Pick "Rows 100", switch a tab, and the list snaps back to 20 with every
 * filter cleared - and because the write also `replace`s the history entry,
 * the browser's Back button can't get them back either.
 *
 * Seeding from `window.location.search` keeps whatever nuqs put there. Reading
 * through `params` stays fine: a component only reads the keys it owns, and
 * those change only via real navigations. It is the WRITE that has to start
 * from the live URL.
 *
 * The write is skipped when the mutation is a no-op, so effects that call this
 * to reconcile the URL don't need their own "did anything change?" guard.
 */
export const useUrlParamWriter = (): ((mutate: (params: URLSearchParams) => void) => void) => {
  const [, setParams] = useSearchParams();

  return useCallback(
    (mutate) => {
      const next = new URLSearchParams(window.location.search);
      const before = next.toString();
      mutate(next);
      if (next.toString() === before) return;
      setParams(next, { replace: true });
    },
    [setParams],
  );
};
